/* MaintenanceTab.js — v6.32.154

   Super Admin → Operations → Maintenance tab.

   Two cards, each wired to /api/super-admin/maintenance/dedupe-* :
     - GridFS revisions cleanup
     - questions collection cleanup

   Flow per card:
     1. [Run Dry-Run] → shows scan summary inline.
     2. After dry-run with non-zero losers, an Execute panel reveals
        with a typed-DB-name confirm input + scary red button.
     3. Execute response shows the receipt path + delete counts.

   Mirrors the CLI's two-step typed-DB-name confirm pattern so the UI
   has the exact same safety net as the terminal scripts.
*/

import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { AlertTriangle, CheckCircle, Database, FileText, Loader2, Power, RefreshCw, ShieldCheck, Activity, Download, Heart } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import MaintenanceAudioToggle from "./MaintenanceAudioToggle";
import ProductionTriageCard from "./ProductionTriageCard";
import { CatalogNamesCard, QuoteMigrationCard } from "./OwnerCleanupCards";
import { playSuccessChime, playFailureChime } from "../utils/maintenanceAudio";

const API = process.env.REACT_APP_BACKEND_URL;

const TONE_STYLES = {
  green: { bar: "bg-emerald-500", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", label: "Healthy" },
  amber: { bar: "bg-amber-500",   text: "text-amber-800",   bg: "bg-amber-50",   border: "border-amber-300", label: "Watch" },
  red:   { bar: "bg-red-600",     text: "text-red-800",     bg: "bg-red-50",     border: "border-red-300",   label: "At limit" },
  gray:  { bar: "bg-slate-400",   text: "text-slate-600",   bg: "bg-slate-50",   border: "border-slate-200", label: "Loading" },
};

function _fmtAge(hours) {
  if (hours === null || hours === undefined) return "—";
  if (hours < 1) return `${Math.max(1, Math.round(hours * 60))}m ago`;
  if (hours < 24) return `${Math.round(hours)}h ago`;
  return `${Math.round(hours / 24)}d ago`;
}

function HealthCheckIndicator() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);

  const fetchStatus = async () => {
    try {
      const r = await axios.get(
        `${API}/api/super-admin/maintenance/health-check-status`,
        { withCredentials: true },
      );
      setStatus(r.data);
    } catch (e) {
      setStatus({ ok: false, state: "error" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (cancelled) return;
      await fetchStatus();
    })();
    return () => { cancelled = true; };
  }, []);

  const runNow = async () => {
    setRunning(true);
    try {
      await axios.post(
        `${API}/api/super-admin/maintenance/run-health-check`,
        {},
        { withCredentials: true },
      );
      await fetchStatus();
    } catch (e) {
      // Swallow — the indicator will fall through to 'unknown' on next fetch
      await fetchStatus();
    } finally {
      setRunning(false);
    }
  };

  let pill;
  if (loading) {
    pill = (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
        data-testid="health-check-indicator-loading"
      >
        <Loader2 size={12} className="animate-spin" />
        Loading…
      </div>
    );
  } else if (!status || status.state === "error") {
    pill = (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-600 border border-slate-200"
        data-testid="health-check-indicator-unknown"
        title="Could not reach the data-state health endpoint."
      >
        <Heart size={12} />
        Status unknown
      </div>
    );
  } else if (status.state === "never_run") {
    pill = (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-slate-100 text-slate-700 border border-slate-200"
        data-testid="health-check-indicator-never-run"
        title="Daily scheduler hasn't completed its first run yet. First run fires within 3 minutes of pod boot, or click Run check now."
      >
        <Heart size={12} />
        Safety net warming up…
      </div>
    );
  } else if (status.state === "drifted") {
    const reasons = (status.last_check_reasons || []).slice(0, 2).join(" · ");
    pill = (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-900 border border-amber-300"
        data-testid="health-check-indicator-drifted"
        title={`Last check at ${status.last_check_at}: ${reasons || "drift detected"}`}
      >
        <AlertTriangle size={12} className="text-amber-600" />
        Drift detected · {_fmtAge(status.hours_since_check)}
        {reasons && <span className="hidden md:inline opacity-75"> · {reasons}</span>}
      </div>
    );
  } else {
    pill = (
      <div
        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-800 border border-emerald-200"
        data-testid="health-check-indicator-healthy"
        title={`Last successful health check at ${status.last_check_at}`}
      >
        <Heart size={12} className="text-emerald-600 fill-emerald-200" />
        Healthy · checked {_fmtAge(status.hours_since_check)}
      </div>
    );
  }

  return (
    <div className="inline-flex items-center gap-1.5">
      {pill}
      <button
        type="button"
        onClick={runNow}
        disabled={running || loading}
        title="Run health check now (bypasses the 24h scheduler)"
        data-testid="health-check-run-now-btn"
        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium bg-white text-slate-700 border border-slate-200 hover:bg-slate-50 hover:border-slate-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
      >
        <RefreshCw size={11} className={running ? "animate-spin" : ""} />
        {running ? "Checking…" : "Run check now"}
      </button>
    </div>
  );
}

function MongoStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchStats = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/super-admin/maintenance/mongo-stats`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const renderMeter = (label, pct, current, limit, tone, sublabel) => {
    if (pct == null) {
      return (
        <div className="space-y-1" data-testid={`mongo-meter-${label.toLowerCase().replace(/\s/g, '-')}`}>
          <div className="flex justify-between text-xs">
            <span className="font-bold text-gray-700">{label}</span>
            <span className="font-mono text-gray-500">unavailable on M0</span>
          </div>
          <div className="h-2 bg-gray-100 rounded">
            <div className="h-2 bg-gray-300 rounded" style={{ width: "0%" }} />
          </div>
        </div>
      );
    }
    const styles = TONE_STYLES[tone] || TONE_STYLES.green;
    return (
      <div className="space-y-1" data-testid={`mongo-meter-${label.toLowerCase().replace(/\s/g, '-')}`}>
        <div className="flex justify-between text-xs">
          <span className="font-bold text-gray-700">{label}</span>
          <span className={`font-mono ${styles.text}`}>
            {current} / {limit} ({pct}%)
          </span>
        </div>
        <div className="h-2 bg-gray-100 rounded overflow-hidden">
          <div className={`h-2 ${styles.bar} rounded transition-all`} style={{ width: `${Math.min(100, pct)}%` }} />
        </div>
        {sublabel && <div className="text-[10px] text-gray-500">{sublabel}</div>}
      </div>
    );
  };

  if (loading && !data) {
    return (
      <div className="bg-white border border-gray-200 rounded p-4" data-testid="mongo-stats-card">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="animate-spin" size={16} />
          Loading Mongo capacity…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-300 rounded p-4" data-testid="mongo-stats-card">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="text-red-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <p className="font-bold">Failed to load Mongo capacity</p>
            <p className="text-xs mt-1 font-mono">{error}</p>
            <Button
              size="sm"
              variant="outline"
              className="mt-2"
              onClick={fetchStats}
              data-testid="mongo-stats-retry"
            >
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const tone = TONE_STYLES[data.overall_tone] || TONE_STYLES.green;
  const u = data.usage;
  const l = data.limits;

  return (
    <div
      className={`bg-white border-2 ${tone.border} rounded p-4`}
      data-testid="mongo-stats-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-2">
          <Activity size={20} className={tone.text} />
          <div>
            <h3 className="text-sm font-bold text-gray-900">
              Atlas M0 Capacity ({l.tier})
            </h3>
            <p className="text-xs text-gray-600">
              Database: <span className="font-mono">{data.db_name}</span>
            </p>
          </div>
        </div>
        <div
          className={`px-2 py-1 rounded text-xs font-bold ${tone.bg} ${tone.text}`}
          data-testid="mongo-stats-tone-badge"
        >
          {tone.label}
        </div>
      </div>

      <div className="space-y-3">
        {renderMeter(
          "Storage",
          u.storage_pct,
          `${u.storage_mb} MB`,
          `${l.storage_mb} MB`,
          u.storage_tone,
          `data ${u.data_mb} MB · indexes ${u.index_mb} MB · ${u.objects.toLocaleString()} objects`,
        )}
        {renderMeter(
          "Collections",
          u.collections_pct,
          u.collections,
          l.max_collections,
          u.collections_tone,
        )}
        {renderMeter(
          "Connections",
          u.connections_pct,
          u.connections_current ?? "—",
          l.max_connections,
          u.connections_tone,
          "May be unavailable on shared M0 clusters",
        )}
      </div>

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
        <span className="text-[10px] text-gray-500 font-mono">
          checked {new Date(data.checked_at).toLocaleTimeString()}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={fetchStats}
          disabled={loading}
          data-testid="mongo-stats-refresh"
        >
          {loading ? <Loader2 className="animate-spin" size={14} /> : "Refresh"}
        </Button>
      </div>

      {data.overall_tone !== "green" && (
        <div className={`mt-3 p-2 ${tone.bg} ${tone.border} border rounded text-xs ${tone.text}`}>
          <p className="font-bold">
            {data.overall_tone === "red" ? "M0 capacity nearly exhausted." : "M0 capacity approaching limit."}
          </p>
          <p className="mt-1">
            Run the Dedupe cards below to reclaim space, or upgrade to Atlas M10 for headroom.
          </p>
        </div>
      )}
    </div>
  );
}


function summaryRow(label, value) {
  return (
    <div className="flex justify-between border-b border-gray-100 py-1">
      <span className="text-xs text-gray-600">{label}</span>
      <span className="text-xs font-mono font-bold text-gray-900">{value}</span>
    </div>
  );
}

function DataStateHealthCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const fetchState = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${API}/api/super-admin/maintenance/data-state`);
      setData(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load data state");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchState();
  }, []);

  if (loading && !data) {
    return (
      <div className="bg-white border border-gray-200 rounded p-4" data-testid="data-state-card">
        <div className="flex items-center gap-2 text-sm text-gray-600">
          <Loader2 className="animate-spin" size={16} />
          Loading data parity status…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-300 rounded p-4" data-testid="data-state-card">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="text-red-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-red-900">
            <p className="font-bold">Failed to load data parity status</p>
            <p className="text-xs mt-1 font-mono">{error}</p>
            <Button size="sm" variant="outline" className="mt-2" onClick={fetchState} data-testid="data-state-retry">
              Retry
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const tone = TONE_STYLES[data.overall_tone] || TONE_STYLES.green;
  const m = data.metrics;
  const a = data.accounts;

  const Metric = ({ label, value, sub, t, testId }) => {
    const tx = TONE_STYLES[t] || { text: "text-gray-600", bg: "bg-gray-50", border: "border-gray-200" };
    return (
      <div className={`p-3 rounded border ${tx.border} ${tx.bg}`} data-testid={testId}>
        <p className={`text-xs font-bold ${tx.text}`}>{label}</p>
        <p className={`text-lg font-mono font-bold ${tx.text}`}>{value}</p>
        {sub && <p className={`text-[10px] ${tx.text} mt-0.5`}>{sub}</p>}
      </div>
    );
  };

  return (
    <div
      className={`bg-white border-2 ${tone.border} rounded p-4`}
      data-testid="data-state-card"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-start gap-2">
          <ShieldCheck size={20} className={tone.text} />
          <div>
            <h3 className="text-sm font-bold text-gray-900">Data State Health (v6.32.191)</h3>
            <p className="text-xs text-gray-600">
              Live parity snapshot of this environment&apos;s data — Rule 1b, ELD/KLU coverage, and account presence.
              Catches drift between code-deployed state and data-migrated state.
            </p>
          </div>
        </div>
        <div
          className={`px-2 py-1 rounded text-xs font-bold ${tone.bg} ${tone.text}`}
          data-testid="data-state-tone-badge"
        >
          {tone.label}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
        <Metric
          label="Total questions"
          value={data.total_items.toLocaleString()}
          t="green"
          testId="data-state-total"
        />
        <Metric
          label="Rule 1b compliance"
          value={m.rule_1b_pct != null ? `${m.rule_1b_pct}%` : "—"}
          sub={m.rule_1b_violations != null ? `${m.rule_1b_violations} violations` : null}
          t={m.rule_1b_tone}
          testId="data-state-rule-1b"
        />
        <Metric
          label="ELD coverage"
          value={`${m.eld_pct}%`}
          t={m.eld_tone}
          testId="data-state-eld"
        />
        <Metric
          label="KLU coverage"
          value={`${m.klu_pct}%`}
          t={m.klu_tone}
          testId="data-state-klu"
        />
        <Metric
          label="ERMA reviewer"
          value={a.erma_reviewer_present ? "✓ active" : "✗ missing"}
          t={a.erma_reviewer_tone}
          testId="data-state-erma"
        />
        <Metric
          label="Demo accounts"
          value={`${a.demo_accounts_present} / ${a.demo_accounts_expected}`}
          t={a.demo_tone}
          testId="data-state-demo"
        />
      </div>

      <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100">
        <span className="text-[10px] text-gray-500 font-mono">
          checked {new Date(data.checked_at).toLocaleTimeString()}
        </span>
        <Button
          size="sm"
          variant="ghost"
          onClick={fetchState}
          disabled={loading}
          data-testid="data-state-refresh"
        >
          {loading ? <Loader2 className="animate-spin" size={14} /> : "Refresh"}
        </Button>
      </div>

      {data.overall_tone !== "green" && (
        <div className={`mt-3 p-2 ${tone.bg} ${tone.border} border rounded text-xs ${tone.text}`}>
          <p className="font-bold">
            Data state out of sync.
          </p>
          <p className="mt-1">
            Run the Production Data Migration card below to bring this environment to parity.
          </p>
        </div>
      )}
    </div>
  );
}


function ProductionDataMigrationCard() {
  const [eldKlu, setEldKlu] = useState({ state: "idle", result: null, error: "" });
  const [erma, setErma] = useState({ state: "idle", result: null, error: "" });
  const [demo, setDemo] = useState({ state: "idle", result: null, error: "" });
  // v6.32.193 — Rule 1b paid cleanup rows. Each carries a `quote` slot so
  // the operator can review the pre-quote before clicking "Confirm spend".
  const [picFix, setPicFix] = useState({ state: "idle", result: null, error: "", quote: null });
  const [psgFix, setPsgFix] = useState({ state: "idle", result: null, error: "", quote: null });
  const [titleFix, setTitleFix] = useState({ state: "idle", result: null, error: "", quote: null });
  // v6.32.196 — Nano Banana fixer for the last 1 K-5 Writing miss
  const [finalPicFix, setFinalPicFix] = useState({ state: "idle", result: null, error: "", quote: null });

  const run = async (path, set, body) => {
    set((s) => ({ ...s, state: "running", error: "" }));
    try {
      const r = await axios.post(`${API}/api/super-admin/maintenance/${path}`, body || {});
      // If backend wants confirmation, surface the quote instead of treating it as a result.
      if (r.data?.confirmation_required) {
        set({ state: "quote", result: null, error: "", quote: r.data });
      } else {
        set({ state: "done", result: r.data, error: "", quote: null });
      }
    } catch (e) {
      set({ state: "error", result: null, error: e?.response?.data?.detail || e.message, quote: null });
    }
  };

  const Row = ({ label, hint, state, result, error, quote, onClick, onConfirm, testId, paid }) => (
    <div className="flex items-start justify-between gap-3 py-3 border-b border-gray-100 last:border-b-0">
      <div className="flex-1 min-w-0">
        <p className="text-sm font-bold text-gray-900">
          {label}
          {paid && <span className="ml-2 inline-block px-1.5 py-0.5 rounded bg-amber-100 text-amber-900 text-[10px] font-bold align-middle">PAID</span>}
        </p>
        <p className="text-xs text-gray-600 mt-0.5">{hint}</p>
        {state === "quote" && quote && (
          <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded text-xs text-amber-900" data-testid={`${testId}-quote`}>
            <p className="font-bold">Confirmation required — ~${quote.estimated_cost_usd?.toFixed(2)} in content-engine spend</p>
            <p className="mt-1">{quote.label} · {quote.estimated_items} items</p>
          </div>
        )}
        {state === "done" && result && (
          <p className="text-xs font-mono text-emerald-700 mt-1" data-testid={`${testId}-result`}>
            ✓ {JSON.stringify(result).slice(0, 200)}
          </p>
        )}
        {state === "error" && (
          <p className="text-xs font-mono text-red-700 mt-1" data-testid={`${testId}-error`}>
            ✗ {error}
          </p>
        )}
      </div>
      {state === "quote" ? (
        <Button
          size="sm"
          onClick={onConfirm}
          data-testid={`${testId}-confirm`}
          className="flex-shrink-0 bg-amber-600 hover:bg-amber-700 text-white"
        >
          Confirm ${quote?.estimated_cost_usd?.toFixed(2)} spend
        </Button>
      ) : (
        <Button
          size="sm"
          onClick={onClick}
          disabled={state === "running"}
          data-testid={testId}
          className="flex-shrink-0"
        >
          {state === "running" ? <Loader2 className="animate-spin" size={14} /> : state === "done" ? "Re-run" : "Run"}
        </Button>
      )}
    </div>
  );

  return (
    <div className="bg-white border-2 border-violet-300 rounded p-4" data-testid="prod-data-migration-card">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-start gap-2 flex-1 min-w-0">
          <Database size={20} className="text-violet-700 flex-shrink-0" />
          <div>
            <h3 className="text-sm font-bold text-gray-900">Production Data Migration (v6.32.191)</h3>
            <p className="text-xs text-gray-600">
              One-click backfills for data that wasn't migrated when v6.32.181→190 deployed.
              ZERO content-engine cost on free rows. Safe to re-run (idempotent).
            </p>
          </div>
        </div>
        <a
          href={`${API}/api/super-admin/maintenance/launch-checklist.pdf`}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="download-launch-checklist"
          className="flex-shrink-0 inline-flex items-center gap-1 px-2 py-1 rounded border border-violet-300 bg-violet-50 hover:bg-violet-100 text-xs font-bold text-violet-900"
        >
          <Download size={12} />
          Checklist PDF
        </a>
      </div>

      <Row
        label="Backfill ELD Standard + Key Language Use tags"
        hint="Closes the 0% → ~100% gap on the public WIDA Fidelity page. Deterministic keyword classification against WIDA's 5 ELD strands and 4 KLU categories."
        state={eldKlu.state}
        result={eldKlu.result}
        error={eldKlu.error}
        testId="run-backfill-eld-klu"
        onClick={() => run("backfill-eld-klu", setEldKlu)}
      />
      <Row
        label="Seed ERMA reviewer account + sample school"
        hint="Activates erma.reviewer@nycps.edu so compliance reviewers can sign in. Requires MOSAIC_ERMA_PW env var. Also seeds the sample school + 5 students + 1 class for review walkthroughs."
        state={erma.state}
        result={erma.result}
        error={erma.error}
        testId="run-seed-erma-reviewer"
        onClick={() => run("seed-erma-reviewer", setErma)}
      />
      <Row
        label="Seed demo isolation accounts (4 roles)"
        hint="Activates demo.district.admin / demo.school.admin / demo.teacher.iso / demo.student.iso so the sales / try-the-demo flow works on production."
        state={demo.state}
        result={demo.result}
        error={demo.error}
        testId="run-seed-demo-accounts"
        onClick={() => run("seed-demo-accounts", setDemo)}
      />

      <div className="mt-2 mb-1 pt-2 border-t border-gray-200">
        <p className="text-xs font-bold text-violet-900">Rule 1b cleanup (v6.32.193)</p>
        <p className="text-[10px] text-gray-600">
          Closes the remaining 253 Rule 1b violations on production. Free deterministic first, paid MOSAIC content engine fallback with explicit confirmation.
        </p>
      </div>

      <Row
        label="Fix K-5 Writing missing picture prompts"
        hint="Deterministic fuzzy-reuse from 213-image library. Items with zero overlap get an EXPLICIT_FALLBACK substitution. ZERO content-engine cost."
        state={picFix.state}
        result={picFix.result}
        error={picFix.error}
        testId="run-fix-writing-pictures"
        onClick={() => run("fix-writing-pictures", setPicFix)}
      />
      <Row
        label="Rewrite Reading passages out of word-count window"
        hint="MOSAIC content engine in batches of 6 (~$0.003/item). Pre-quotes total spend before running."
        state={psgFix.state}
        result={psgFix.result}
        error={psgFix.error}
        quote={psgFix.quote}
        testId="run-fix-reading-passages"
        paid
        onClick={() => run("fix-reading-passages", setPsgFix)}
        onConfirm={() => run("fix-reading-passages", setPsgFix, { confirm: true })}
      />
      <Row
        label="Backfill missing Reading passage titles"
        hint="Phase 1 deterministic first-line sniff at $0; Phase 2 MOSAIC content engine authoring for what remains (~$0.003/item). Pre-quotes Phase 2."
        state={titleFix.state}
        result={titleFix.result}
        error={titleFix.error}
        quote={titleFix.quote}
        testId="run-fix-reading-titles"
        paid
        onClick={() => run("fix-reading-titles", setTitleFix)}
        onConfirm={() => run("fix-reading-titles", setTitleFix, { confirm: true })}
      />
      <Row
        label="Generate the final missing K-5 Writing picture (MOSAIC image engine)"
        hint="Catches the 1 item that fuzzy-reuse couldn't match. Generates a MOSAIC image engine image (~$0.50/item) and serves it via /api/bp-images/{slug} — no second deploy needed."
        state={finalPicFix.state}
        result={finalPicFix.result}
        error={finalPicFix.error}
        quote={finalPicFix.quote}
        testId="run-fix-final-picture-prompt"
        paid
        onClick={() => run("fix-final-picture-prompt", setFinalPicFix)}
        onConfirm={() => run("fix-final-picture-prompt", setFinalPicFix, { confirm: true })}
      />

      <p className="text-[10px] text-gray-500 mt-3">
        Each run logs an audit_logs row keyed to your super_admin email.
        Paid endpoints pre-quote and require a second click to authorize the spend.
      </p>
    </div>
  );
}

function CleanupCard({
  title,
  description,
  endpoint,
  testIdPrefix,
  renderSummary,
  extraBodyOnExecute = {},
}) {
  const [scanLoading, setScanLoading] = useState(false);
  const [execLoading, setExecLoading] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [execResult, setExecResult] = useState(null);
  const [confirmName, setConfirmName] = useState("");
  const [error, setError] = useState("");

  const dbName = scanResult?.db_name || "";
  const losers = scanResult?.summary?.losers_total ?? null;
  const canExecute = losers != null && losers > 0;

  const runDryRun = async () => {
    setError("");
    setExecResult(null);
    setScanLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/super-admin/maintenance/${endpoint}`,
        { execute: false }
      );
      setScanResult(res.data);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError(
        `${status ? `[HTTP ${status}] ` : ""}${detail || e.message || "Dry-run failed."}`
      );
    } finally {
      setScanLoading(false);
    }
  };

  const runExecute = async () => {
    setError("");
    setExecLoading(true);
    try {
      const res = await axios.post(
        `${API}/api/super-admin/maintenance/${endpoint}`,
        { execute: true, confirm_db_name: confirmName, ...extraBodyOnExecute }
      );
      setExecResult(res.data);
      setConfirmName("");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      setError(
        `${status ? `[HTTP ${status}] ` : ""}${detail || e.message || "Execute failed."}`
      );
    } finally {
      setExecLoading(false);
    }
  };

  return (
    <div
      className="bg-white border border-gray-200 p-5 shadow-sm"
      data-testid={`${testIdPrefix}-card`}
    >
      <div className="flex items-center gap-2 mb-2">
        <Database size={18} className="text-blue-700" />
        <h3 className="text-base font-bold text-gray-900">{title}</h3>
      </div>
      <p className="text-sm text-gray-600 mb-4">{description}</p>

      <Button
        type="button"
        onClick={runDryRun}
        disabled={scanLoading}
        variant="outline"
        className="h-9 mb-3"
        data-testid={`${testIdPrefix}-dryrun-btn`}
      >
        {scanLoading ? (
          <Loader2 size={14} className="mr-2 animate-spin" />
        ) : (
          <ShieldCheck size={14} className="mr-2" />
        )}
        Run Dry-Run (safe — no deletes)
      </Button>

      {scanResult && (
        <div
          className="bg-gray-50 border border-gray-200 p-3 mb-3"
          data-testid={`${testIdPrefix}-summary`}
        >
          <div className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2">
            Scan Summary · DB: <span className="font-mono">{dbName}</span>
          </div>
          {renderSummary(scanResult.summary)}
          {losers === 0 && (
            <div
              className="mt-3 flex items-center gap-2 text-emerald-700 text-sm font-bold"
              data-testid={`${testIdPrefix}-clean`}
            >
              <CheckCircle size={16} /> Already clean — nothing to delete.
            </div>
          )}
        </div>
      )}

      {canExecute && (
        <div
          className="bg-red-50 border-2 border-red-300 p-3"
          data-testid={`${testIdPrefix}-execute-panel`}
        >
          <div className="flex items-center gap-2 text-red-800 font-bold mb-2">
            <AlertTriangle size={16} />
            <span>Execute Real Delete</span>
          </div>
          <p className="text-xs text-red-700 mb-3">
            About to delete <span className="font-mono font-bold">{losers}</span>{" "}
            loser row(s) from <span className="font-mono">{dbName}</span>. A
            JSON receipt with full rollback data will be written to{" "}
            <span className="font-mono">/app/memory/</span>. Type the DB name
            EXACTLY to confirm:
          </p>
          <div className="flex gap-2">
            <Input
              type="text"
              value={confirmName}
              onChange={(e) => setConfirmName(e.target.value)}
              placeholder={dbName}
              className="h-9 font-mono text-sm"
              data-testid={`${testIdPrefix}-confirm-input`}
              disabled={execLoading}
            />
            <Button
              type="button"
              onClick={runExecute}
              disabled={execLoading || confirmName !== dbName}
              className="h-9 bg-red-600 hover:bg-red-700 text-white"
              data-testid={`${testIdPrefix}-execute-btn`}
            >
              {execLoading ? (
                <Loader2 size={14} className="mr-2 animate-spin" />
              ) : (
                <AlertTriangle size={14} className="mr-2" />
              )}
              Delete {losers} Row{losers === 1 ? "" : "s"}
            </Button>
          </div>
        </div>
      )}

      {execResult && (
        <div
          className="mt-3 bg-emerald-50 border-2 border-emerald-300 p-3"
          data-testid={`${testIdPrefix}-result`}
        >
          <div className="flex items-center gap-2 text-emerald-800 font-bold mb-2">
            <CheckCircle size={16} />
            <span>Cleanup Complete</span>
          </div>
          <pre className="text-xs font-mono text-emerald-900 whitespace-pre-wrap">
            {JSON.stringify(execResult.result, null, 2)}
          </pre>
        </div>
      )}

      {error && (
        <div
          className="mt-3 bg-amber-50 border border-amber-300 p-3 text-sm text-amber-900"
          data-testid={`${testIdPrefix}-error`}
        >
          <span className="font-bold">Error:</span> {error}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// v6.32.224 — Flyer Scan Attribution Card
// Surfaces ONLY visits that arrived at /try-demo?src=flyer (i.e. someone
// scanned the QR code on the printed Principal Sneak-Peek Flyer). Other
// paths into /try-demo are NOT counted here. No PII; the underlying
// attribution_hits collection stores only sha256-hashed visitor IDs and
// salted IP hashes.
// ─────────────────────────────────────────────────────────────
function FlyerAttributionCard() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [windowDays, setWindowDays] = useState(30);

  const load = async (days = windowDays) => {
    setLoading(true);
    setError(null);
    try {
      const res = await axios.get(`${API}/api/attribution/summary`, {
        params: { source: "flyer", days },
      });
      setSummary(res.data);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Failed to load attribution stats");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(windowDays);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [windowDays]);

  const max = summary?.daily?.reduce((m, d) => Math.max(m, d.count || 0), 0) || 0;

  return (
    <div className="bg-white border border-slate-200 rounded p-4" data-testid="flyer-attribution-card">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2">
            <Activity size={16} className="text-[#002FA7]" />
            Flyer QR Scan Attribution
          </h3>
          <p className="text-[11px] text-gray-500 mt-1 leading-snug">
            Counts only visits to <span className="font-mono">/try-demo?src=flyer</span>{" "}
            (Principal Sneak-Peek Flyer QR). No PII stored — sha256-hashed visitor IDs only.
          </p>
        </div>
        <div className="flex items-center gap-1">
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setWindowDays(d)}
              className={`text-[11px] px-2 py-1 border ${
                windowDays === d
                  ? "bg-[#002FA7] text-white border-[#002FA7]"
                  : "bg-white text-gray-700 border-gray-300 hover:border-[#002FA7]/40"
              }`}
              data-testid={`flyer-window-${d}d-btn`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => load()}
            className="ml-1 text-gray-400 hover:text-[#002FA7] p-1"
            title="Refresh"
            data-testid="flyer-attribution-refresh-btn"
          >
            <RefreshCw size={13} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {error && (
        <div className="text-xs text-red-600 bg-red-50 border border-red-200 p-2 mb-2" data-testid="flyer-attribution-error">
          {error}
        </div>
      )}

      {summary && !loading && (
        <>
          <div className="grid grid-cols-4 gap-2 mb-3">
            <div className="border border-gray-200 p-2">
              <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Scans</div>
              <div className="text-2xl font-bold text-[#002FA7]" data-testid="flyer-total-scans">
                {summary.total_scans}
              </div>
              <div className="text-[10px] text-gray-400">deduped per visitor/day</div>
            </div>
            <div className="border border-gray-200 p-2">
              <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Unique</div>
              <div className="text-2xl font-bold text-gray-800" data-testid="flyer-unique-visitors">
                {summary.unique_visitors}
              </div>
              <div className="text-[10px] text-gray-400">distinct devices</div>
            </div>
            <div className="border border-gray-200 p-2">
              <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Conv.</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="flyer-conversions">
                {summary.conversions}
              </div>
              <div className="text-[10px] text-gray-400">scan → demo</div>
            </div>
            <div className="border border-gray-200 p-2">
              <div className="text-[10px] uppercase text-gray-500 font-semibold tracking-wider">Rate</div>
              <div className="text-2xl font-bold text-emerald-700" data-testid="flyer-conversion-rate">
                {summary.conversion_rate_pct}%
              </div>
              <div className="text-[10px] text-gray-400">of scans</div>
            </div>
          </div>

          {/* Daily sparkline — bare-bones CSS bar chart, no extra deps. */}
          <div className="border border-gray-200 p-3" data-testid="flyer-attribution-sparkline">
            <div className="flex items-end gap-[2px] h-12">
              {summary.daily.map((d) => {
                const h = max > 0 ? Math.max(2, Math.round((d.count / max) * 48)) : 2;
                return (
                  <div
                    key={d.date}
                    className={`flex-1 ${d.count > 0 ? "bg-[#002FA7]" : "bg-gray-200"}`}
                    style={{ height: `${h}px` }}
                    title={`${d.date}: ${d.count} scan${d.count === 1 ? "" : "s"}`}
                  />
                );
              })}
            </div>
            <div className="flex justify-between mt-1 text-[10px] text-gray-400">
              <span>{summary.daily[0]?.date?.slice(5)}</span>
              <span>last {summary.window_days} days</span>
              <span>{summary.daily[summary.daily.length - 1]?.date?.slice(5)}</span>
            </div>
          </div>

          {summary.total_scans === 0 && (
            <div className="text-[11px] text-gray-500 mt-2 italic">
              No scans yet. Once principals start scanning the QR code on the
              printed flyer, hits will land here within seconds.
            </div>
          )}
        </>
      )}

      {loading && !summary && (
        <div className="text-xs text-gray-400 py-4 text-center" data-testid="flyer-attribution-loading">
          <Loader2 size={14} className="animate-spin inline-block mr-1" /> Loading attribution stats…
        </div>
      )}
    </div>
  );
}

// v6.32.229 — Cross-Modality Anchor Audit Card (WIDA Rule 6 enforcement)
function CrossModalityAnchorCard() {
  const [scan, setScan] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [quote, setQuote] = useState(null);
  const [alignState, setAlignState] = useState("idle"); // idle | scanning | quote | applying | done | error
  const [result, setResult] = useState(null);
  const [errMsg, setErrMsg] = useState(null);

  const fetchAudit = async () => {
    setScanLoading(true);
    setScanError(null);
    try {
      const r = await axios.get(`${API}/api/bp-auditor/cross-modality-anchor-audit`,
        { withCredentials: true });
      setScan(r.data);
    } catch (e) {
      setScanError(e?.response?.data?.detail || e.message || "Audit failed");
    } finally {
      setScanLoading(false);
    }
  };

  const runDryRun = async () => {
    setAlignState("scanning");
    setErrMsg(null);
    try {
      const r = await axios.post(`${API}/api/bp-auditor/align-cross-modality-anchors`,
        { confirm: false }, { withCredentials: true });
      setQuote(r.data);
      setAlignState(r.data.bp_updates_planned + r.data.gs_updates_planned > 0 ? "quote" : "done");
      if (r.data.bp_updates_planned + r.data.gs_updates_planned === 0) {
        setResult({ already_aligned: true });
      }
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e.message || "Dry-run failed");
      setAlignState("error");
    }
  };

  const applyAlignment = async () => {
    setAlignState("applying");
    setErrMsg(null);
    try {
      const r = await axios.post(`${API}/api/bp-auditor/align-cross-modality-anchors`,
        { confirm: true }, { withCredentials: true });
      setResult(r.data);
      setAlignState("done");
      // Refresh the read-only audit so the badge updates in place.
      fetchAudit();
    } catch (e) {
      setErrMsg(e?.response?.data?.detail || e.message || "Apply failed");
      setAlignState("error");
    }
  };

  useEffect(() => { fetchAudit(); }, []);

  const tone = scan
    ? (scan.bp_drifted + scan.gs_drifted === 0 ? "green" : "amber")
    : "green";
  const t = TONE_STYLES[tone];

  return (
    <div className={`bg-white border ${t.border} rounded-lg p-4`}
         data-testid="cross-modality-anchor-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <ShieldCheck size={18} className={t.text} />
          <h3 className="text-sm font-bold text-gray-900">
            Cross-Modality Anchor Audit · WIDA Rule 6
          </h3>
          {scan && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${t.bg} ${t.text} border ${t.border}`}
                  data-testid="cross-modality-tone-badge">
              {scan.bp_drifted + scan.gs_drifted === 0 ? "Aligned" : "Drift detected"}
            </span>
          )}
        </div>
        <button onClick={fetchAudit}
                disabled={scanLoading}
                className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50 flex items-center gap-1"
                data-testid="cross-modality-refresh-btn">
          <RefreshCw size={12} className={scanLoading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <p className="text-xs text-gray-600 mb-3">
        Every <span className="font-mono">(grade, big_picture_context)</span> group containing 2+
        modalities must share the same <span className="font-mono">bp_image_url</span> and
        <span className="font-mono"> graphic_support_url</span>. WIDA Rule 6 (Big-Picture Context).
      </p>

      {scanError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3"
             data-testid="cross-modality-scan-error">
          {scanError}
          <button onClick={fetchAudit} className="ml-2 underline">retry</button>
        </div>
      )}

      {scan && (
        <div className="grid grid-cols-4 gap-2 mb-3 text-center" data-testid="cross-modality-stats">
          <Stat label="Groups" value={scan.total_multi_modality_groups} testid="cm-groups" />
          <Stat label="Aligned" value={scan.aligned}
                tone={scan.aligned === scan.total_multi_modality_groups ? "green" : "amber"}
                testid="cm-aligned" />
          <Stat label="BP drift" value={scan.bp_drifted}
                tone={scan.bp_drifted === 0 ? "green" : "red"} testid="cm-bp-drift" />
          <Stat label="GS drift" value={scan.gs_drifted}
                tone={scan.gs_drifted === 0 ? "green" : "red"} testid="cm-gs-drift" />
        </div>
      )}

      {scan && scan.bp_drifted + scan.gs_drifted > 0 && alignState === "idle" && (
        <Button onClick={runDryRun} size="sm"
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="cross-modality-dry-run-btn">
          Preview alignment ({scan.bp_drifted + scan.gs_drifted} drifted groups)
        </Button>
      )}

      {alignState === "scanning" && (
        <div className="text-xs text-gray-500" data-testid="cross-modality-scanning">
          <Loader2 size={12} className="animate-spin inline-block mr-1" /> Computing alignment plan…
        </div>
      )}

      {alignState === "quote" && quote && (
        <div className="bg-amber-50 border border-amber-300 rounded p-3"
             data-testid="cross-modality-quote">
          <div className="text-xs font-bold text-amber-900 mb-1">Confirm alignment</div>
          <div className="text-xs text-amber-800 mb-2">
            About to update <b>{quote.bp_updates_planned}</b> bp_image_url fields and
            {" "}<b>{quote.gs_updates_planned}</b> graphic_support_url fields across
            {" "}<b>{quote.affected_groups}</b> drifted groups. Listening anchor wins as the
            canonical (Listening &gt; Reading &gt; Speaking &gt; Writing). Receipt JSON written
            to <span className="font-mono">/app/memory/</span> for rollback.
          </div>
          <div className="flex gap-2">
            <Button onClick={applyAlignment} size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="cross-modality-confirm-btn">
              Align {quote.bp_updates_planned + quote.gs_updates_planned} records
            </Button>
            <Button onClick={() => { setAlignState("idle"); setQuote(null); }} size="sm" variant="outline"
                    data-testid="cross-modality-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {alignState === "applying" && (
        <div className="text-xs text-gray-500" data-testid="cross-modality-applying">
          <Loader2 size={12} className="animate-spin inline-block mr-1" /> Aligning anchors…
        </div>
      )}

      {alignState === "done" && result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900"
             data-testid="cross-modality-result">
          {result.already_aligned ? (
            <span><CheckCircle size={12} className="inline-block mr-1" /> Already aligned — no updates needed.</span>
          ) : (
            <>
              <CheckCircle size={12} className="inline-block mr-1" />
              <b>Aligned {result.bp_updates_applied + result.gs_updates_applied} records</b>
              {" "}({result.bp_updates_applied} bp + {result.gs_updates_applied} gs across
              {" "}{result.affected_groups} groups). Post-audit:
              {" "}{result.post_audit?.aligned}/{result.post_audit?.total_multi_modality_groups} groups aligned.
              {result.receipt_path && (
                <div className="mt-1 font-mono text-[10px] text-emerald-700 break-all">
                  Receipt: {result.receipt_path}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {alignState === "error" && errMsg && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"
             data-testid="cross-modality-error">
          {errMsg}
          <button onClick={() => setAlignState("idle")} className="ml-2 underline">dismiss</button>
        </div>
      )}
    </div>
  );
}

// v6.32.231 — Bulk Missing-File Fixer card. Closes the student-facing
// gap where db.questions.bp_image_url points at a file that doesn't
// exist on disk. Sister to the per-scene "Fix Image" button on the BP
// Auditor results page; this batches them.
// v6.32.233 — now polls /bulk-fix-status for live progress (v6.32.232
// made the backend async-detached). Operator sees a real-time progress
// strip with current_scene + Nano Banana / GPT-1 counters instead of
// a 30-min spinner.
function BulkMissingFilesCard() {
  const [scan, setScan] = useState(null);
  const [scanLoading, setScanLoading] = useState(false);
  const [scanError, setScanError] = useState(null);
  const [phase, setPhase] = useState("idle"); // idle | quote | applying | done | error
  const [quote, setQuote] = useState(null);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  const [runId, setRunId] = useState(null);
  const [liveStatus, setLiveStatus] = useState(null);

  const fetchScan = async () => {
    setScanLoading(true);
    setScanError(null);
    try {
      const r = await axios.get(`${API}/api/bp-auditor/missing-files-audit`,
        { withCredentials: true });
      setScan(r.data);
    } catch (e) {
      setScanError(e?.response?.data?.detail || e.message || "Scan failed");
    } finally {
      setScanLoading(false);
    }
  };

  // v6.32.233 — poll /bulk-fix-status while a worker is running.
  useEffect(() => {
    if (!runId) return undefined;
    let cancelled = false;
    const tick = async () => {
      try {
        const { data } = await axios.get(`${API}/api/bp-auditor/bulk-fix-status`,
          { withCredentials: true, params: { run_id: runId }, timeout: 10000 });
        if (cancelled) return;
        setLiveStatus(data);
        if (data?.status === "complete" || data?.status === "failed") {
          setResult({
            total_applied: (data.nb_applied ?? 0) + (data.gpt1_applied ?? 0),
            missing_scenes_at_start: data.missing_scenes_at_start ?? 0,
            nb_applied: data.nb_applied ?? 0,
            gpt1_applied: data.gpt1_applied ?? 0,
            still_missing: data.still_missing ?? 0,
            write_failures: data.write_failures ?? 0,
            receipt_path: data.receipt_path,
            post_scan: data.post_scan,
            already_aligned: false,
          });
          setPhase(data.status === "complete" ? "done" : "error");
          if (data.status === "failed") {
            setErr(data.failure_reason || "Worker failed");
            playFailureChime();  // v6.34.4
          } else {
            playSuccessChime();  // v6.34.4
          }
          setRunId(null);
          fetchScan();
        }
      } catch (_) {
        // Transient errors fine; keep polling.
      }
    };
    tick();
    const id = setInterval(tick, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [runId]);

  const dryRun = async () => {
    setPhase("quote");
    setErr(null);
    try {
      const r = await axios.post(`${API}/api/bp-auditor/bulk-fix-missing-files`,
        { confirm: false }, { withCredentials: true });
      setQuote(r.data);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Dry-run failed");
      setPhase("error");
    }
  };

  const apply = async () => {
    setPhase("applying");
    setErr(null);
    setLiveStatus(null);
    try {
      // v6.32.232 — backend returns 202 immediately with run_id.
      // The polling effect above takes it from there.
      const r = await axios.post(`${API}/api/bp-auditor/bulk-fix-missing-files`,
        { confirm: true }, { withCredentials: true, timeout: 30000 });
      if (r.data.accepted && r.data.run_id) {
        setRunId(r.data.run_id);
      } else {
        // Legacy synchronous fallback (pre-232).
        setResult(r.data);
        setPhase("done");
        fetchScan();
      }
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Bulk fix failed");
      setPhase("error");
    }
  };

  useEffect(() => { fetchScan(); }, []);

  const missingCount = scan?.missing?.length ?? 0;
  const tone = scan ? (missingCount === 0 ? "green" : "red") : "gray";
  const t = TONE_STYLES[tone] || TONE_STYLES.gray;

  return (
    <div className={`bg-white border ${t.border} rounded-lg p-4`}
         data-testid="bulk-missing-files-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText size={18} className={t.text} />
          <h3 className="text-sm font-bold text-gray-900">
            Missing BP Image Files · Bulk Fixer
          </h3>
          {scan && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${t.bg} ${t.text} border ${t.border}`}
                  data-testid="bulk-missing-tone-badge">
              {missingCount === 0 ? "All files present" : `${missingCount} missing`}
            </span>
          )}
        </div>
        <button onClick={fetchScan} disabled={scanLoading}
                className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50 flex items-center gap-1"
                data-testid="bulk-missing-refresh-btn">
          <RefreshCw size={12} className={scanLoading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <p className="text-xs text-gray-600 mb-3">
        Scans every <span className="font-mono">db.questions.bp_image_url</span> for files that
        don't exist on disk. Missing files render as broken icons in the digital exam and blank
        boxes in the BLM PDF. Bulk regenerator uses MOSAIC image engine → MOSAIC image engine fallback,
        per-scene timeout protected, dedupes by (grade, context).
      </p>

      {scanError && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3"
             data-testid="bulk-missing-scan-error">
          {scanError}
          <button onClick={fetchScan} className="ml-2 underline">retry</button>
        </div>
      )}

      {scan && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-center" data-testid="bulk-missing-stats">
          <Stat label="Total URLs" value={scan.total_unique_urls} testid="bm-total" />
          <Stat label="Present" value={scan.present}
                tone={scan.present === scan.total_unique_urls ? "green" : "amber"}
                testid="bm-present" />
          <Stat label="Missing" value={missingCount}
                tone={missingCount === 0 ? "green" : "red"} testid="bm-missing" />
        </div>
      )}

      {scan && missingCount > 0 && phase === "idle" && (
        <Button onClick={dryRun} size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-missing-dry-run-btn">
          Preview bulk fix ({missingCount} scene{missingCount === 1 ? "" : "s"})
        </Button>
      )}

      {phase === "quote" && !quote && (
        <div className="text-xs text-gray-500" data-testid="bulk-missing-quoting">
          <Loader2 size={12} className="animate-spin inline-block mr-1" /> Computing plan…
        </div>
      )}

      {phase === "quote" && quote && (
        <div className="bg-red-50 border border-red-300 rounded p-3" data-testid="bulk-missing-quote">
          <div className="text-xs font-bold text-red-900 mb-1">Confirm bulk regeneration</div>
          <div className="text-xs text-red-800 mb-2">
            About to regenerate <b>{quote.missing_count}</b> missing BP image files via Nano
            Banana → MOSAIC image engine fallback. Expected duration: ~{Math.ceil(quote.missing_count * 1.5)}–{Math.ceil(quote.missing_count * 4)} min
            (per-scene timeout 600s, server-side asyncio.wait_for protected). Each scene's
            siblings across all 4 modalities will resolve in one regen thanks to the v6.32.229
            cross-modality anchor work. Receipt JSON written to <span className="font-mono">/app/memory/</span> for rollback.
          </div>
          <div className="flex gap-2">
            <Button onClick={apply} size="sm"
                    className="bg-emerald-600 hover:bg-emerald-700 text-white"
                    data-testid="bulk-missing-confirm-btn">
              Regenerate {quote.missing_count} file{quote.missing_count === 1 ? "" : "s"}
            </Button>
            <Button onClick={() => { setPhase("idle"); setQuote(null); }} size="sm" variant="outline"
                    data-testid="bulk-missing-cancel-btn">
              Cancel
            </Button>
          </div>
        </div>
      )}

      {phase === "applying" && (
        <div className="text-xs text-gray-700 bg-amber-50 border border-amber-200 rounded p-3"
             data-testid="bulk-missing-applying">
          <Loader2 size={12} className="animate-spin inline-block mr-1" />
          {liveStatus ? (
            <>
              <b>{liveStatus.processed ?? 0}/{liveStatus.missing_scenes_at_start ?? "?"}</b> processed
              {" · "}NB <b>{liveStatus.nb_applied ?? 0}</b>
              {" · "}GPT-1 <b>{liveStatus.gpt1_applied ?? 0}</b>
              {" · "}stubborn <b>{liveStatus.still_missing ?? 0}</b>
              {liveStatus.current_scene && (
                <div className="mt-1 font-mono text-[10px] text-amber-800 break-words"
                     data-testid="bulk-missing-current-scene">
                  Now: {liveStatus.current_scene}
                </div>
              )}
            </>
          ) : (
            <>Queued… spawning detached worker.</>
          )}
        </div>
      )}

      {phase === "done" && result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900"
             data-testid="bulk-missing-result">
          <CheckCircle size={12} className="inline-block mr-1" />
          <b>Regenerated {result.total_applied} of {result.missing_scenes_at_start} missing
          file{result.missing_scenes_at_start === 1 ? "" : "s"}</b>
          {" "}(MOSAIC image engine: {result.nb_applied} · mosaic-image-engine: {result.gpt1_applied}
          · still-stubborn: {result.still_missing}
          · write-errors: {result.write_failures}).
          {" "}Post-scan: {result.post_scan?.missing} still missing.
          {result.receipt_path && (
            <div className="mt-1 font-mono text-[10px] text-emerald-700 break-all">
              Receipt: {result.receipt_path}
            </div>
          )}
        </div>
      )}

      {phase === "error" && err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2"
             data-testid="bulk-missing-error">
          {err}
          <button onClick={() => setPhase("idle")} className="ml-2 underline">dismiss</button>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value, tone = "gray", testid }) {
  const colors = {
    gray: "bg-gray-50 border-gray-200 text-gray-900",
    green: "bg-emerald-50 border-emerald-200 text-emerald-900",
    amber: "bg-amber-50 border-amber-300 text-amber-900",
    red: "bg-red-50 border-red-300 text-red-900",
  };
  return (
    <div className={`border rounded p-2 ${colors[tone] || colors.gray}`} data-testid={testid}>
      <div className="text-lg font-bold">{value ?? "—"}</div>
      <div className="text-[10px] uppercase tracking-wide opacity-70">{label}</div>
    </div>
  );
}

// v6.32.230 — Re-Verify-All kill switch card. Surfaces live status with
// a tone-coded pill ('running' / 'idle' / 'failed' / 'cancelled') and a
// two-click 'Cancel stuck Re-Verify' button that hits the v6.32.118
// /reverify-all/cancel-stuck endpoint (now also flips Mongo rows
// immediately per v6.32.230). Operator escape hatch — no need to wait
// 30 min for the watchdog when the worker is obviously dead.
function ReverifyKillSwitchCard() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState(null);
  // v6.34.4 — track the previous terminal status so we can chime on
  // transition (running → complete / failed / cancelled). Without this
  // ref the chime would re-fire on every poll once the run was already
  // terminal. Initialized lazily on first fetch.
  const prevStatusRef = useRef(null);

  const fetchStatus = async () => {
    setLoading(true);
    setErr(null);
    try {
      const r = await axios.get(`${API}/api/bp-auditor/reverify-all/status`,
        { withCredentials: true });
      const next = r.data;
      const prev = prevStatusRef.current;
      // Chime on running→terminal transition only (not on every poll).
      if (prev && ["running", "starting", "queued"].includes(prev) &&
          ["complete", "failed", "cancelled"].includes(next?.status)) {
        if (next.status === "complete") {
          playSuccessChime();
        } else {
          playFailureChime();
        }
      }
      prevStatusRef.current = next?.status || null;
      setStatus(next);
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Status fetch failed");
    } finally {
      setLoading(false);
    }
  };

  const doCancel = async () => {
    setCancelling(true);
    setErr(null);
    try {
      const r = await axios.post(`${API}/api/bp-auditor/reverify-all/cancel-stuck`,
        {}, { withCredentials: true });
      setResult(r.data);
      setConfirming(false);
      fetchStatus();
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message || "Cancel failed");
    } finally {
      setCancelling(false);
    }
  };

  useEffect(() => { fetchStatus(); }, []);

  const s = status?.status || "unknown";
  const isLive = ["running", "starting", "queued"].includes(s);
  const isStale = (() => {
    if (!status?.updated_at || !isLive) return false;
    try {
      const ageMs = Date.now() - new Date(status.updated_at).getTime();
      return ageMs > 10 * 60 * 1000; // 10 min — well before the 30-min watchdog
    } catch { return false; }
  })();

  let tone = "green";
  if (s === "failed" || isStale) tone = "red";
  else if (s === "cancelled") tone = "gray";
  else if (isLive) tone = "amber";
  const tStyle = TONE_STYLES[tone] || TONE_STYLES.gray;

  return (
    <div className={`bg-white border ${tStyle.border} rounded-lg p-4`}
         data-testid="reverify-kill-switch-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Power size={18} className={tStyle.text} />
          <h3 className="text-sm font-bold text-gray-900">
            BP Re-Verify-All Kill Switch
          </h3>
          {status && (
            <span className={`text-xs px-2 py-0.5 rounded-full ${tStyle.bg} ${tStyle.text} border ${tStyle.border}`}
                  data-testid="reverify-status-pill">
              {s}{isStale ? " · stale" : ""}
            </span>
          )}
        </div>
        <button onClick={fetchStatus} disabled={loading}
                className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-50 flex items-center gap-1"
                data-testid="reverify-status-refresh-btn">
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <p className="text-xs text-gray-600 mb-3">
        Operator escape hatch. Cancels any wedged Re-Verify-All run (file + Mongo) so a fresh
        attempt can spawn. Watchdog already auto-marks runs with no heartbeat for 30+ min as
        failed; this gives you immediate control when you don't want to wait.
      </p>

      {status && (
        <div className="grid grid-cols-3 gap-2 mb-3 text-center" data-testid="reverify-stats">
          <Stat label="Processed" value={`${status.processed ?? 0}/${status.total ?? "—"}`} testid="rk-processed" />
          <Stat label="Stubborn" value={(status.stubborn || []).length}
                tone={(status.stubborn || []).length > 0 ? "amber" : "green"} testid="rk-stubborn" />
          <Stat label="Timeouts" value={status.scene_timeouts ?? 0}
                tone={(status.scene_timeouts ?? 0) > 0 ? "red" : "green"} testid="rk-timeouts" />
        </div>
      )}

      {status?.current_scene && isLive && (
        <div className="text-xs text-gray-600 mb-2 font-mono break-words"
             data-testid="reverify-current-scene">
          <Loader2 size={12} className="animate-spin inline-block mr-1" />
          {status.current_scene}
        </div>
      )}

      {err && (
        <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded p-2 mb-3"
             data-testid="reverify-kill-error">
          {err}
        </div>
      )}

      {(isLive || isStale || s === "failed") && !confirming && (
        <Button onClick={() => setConfirming(true)} size="sm"
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="reverify-cancel-btn">
          <Power size={12} className="inline-block mr-1" /> Cancel stuck Re-Verify
        </Button>
      )}

      {confirming && (
        <div className="bg-red-50 border border-red-300 rounded p-3"
             data-testid="reverify-cancel-confirm">
          <div className="text-xs font-bold text-red-900 mb-1">Confirm cancellation</div>
          <div className="text-xs text-red-800 mb-2">
            Wipes <span className="font-mono">/tmp/bp_reverify_all_status.json</span> and flips every
            Mongo <span className="font-mono">bp_reverify_runs</span> row in
            <span className="font-mono"> &#123;running, starting, queued&#125;</span> →
            <span className="font-mono"> cancelled</span>. Any actively-running subprocess will
            keep going until it exits naturally but its progress will no longer be tracked.
            Audit-logged.
          </div>
          <div className="flex gap-2">
            <Button onClick={doCancel} disabled={cancelling} size="sm"
                    className="bg-red-600 hover:bg-red-700 text-white disabled:opacity-50"
                    data-testid="reverify-cancel-confirm-btn">
              {cancelling ? <><Loader2 size={12} className="animate-spin inline-block mr-1" /> Cancelling…</> : "Yes, cancel"}
            </Button>
            <Button onClick={() => setConfirming(false)} size="sm" variant="outline"
                    data-testid="reverify-cancel-abort-btn">
              Keep running
            </Button>
          </div>
        </div>
      )}

      {result && (
        <div className="bg-emerald-50 border border-emerald-200 rounded p-3 text-xs text-emerald-900 mt-3"
             data-testid="reverify-cancel-result">
          <CheckCircle size={12} className="inline-block mr-1" />
          Cancelled. File: <b>{result.cancelled ? "wiped" : "not present"}</b>.
          Mongo rows flipped: <b>{result.mongo_rows_cancelled ?? 0}</b>.
          Prior status: <span className="font-mono">{result.prior_status || "—"}</span>.
        </div>
      )}

      {!status && !err && (
        <div className="text-xs text-gray-400" data-testid="reverify-status-loading">
          <Loader2 size={14} className="animate-spin inline-block mr-1" /> Loading status…
        </div>
      )}
    </div>
  );
}


function SyncQuestionsSnapshotCard() {
  const [scanning, setScanning] = useState(false);
  const [applying, setApplying] = useState(false);
  const [scan, setScan] = useState(null);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [lockdown, setLockdown] = useState(null);

  const endpoint = `${API}/api/super-admin/maintenance/sync-questions-from-snapshot`;
  const lockdownEndpoint = `${API}/api/super-admin/maintenance/parity-lockdown-status`;

  const checkLockdown = async () => {
    try {
      const r = await axios.get(lockdownEndpoint, { withCredentials: true });
      setLockdown(r.data);
    } catch (e) {
      setLockdown({ error: e.response?.data?.detail || e.message });
    }
  };

  useEffect(() => { checkLockdown(); }, []);

  const runScan = async () => {
    setScanning(true); setError(null); setResult(null);
    try {
      const r = await axios.post(endpoint, {}, { withCredentials: true });
      setScan(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Scan failed");
    } finally { setScanning(false); }
  };

  const apply = async () => {
    if (!confirm(
      `Replace ${scan?.current_prod_count ?? "?"} questions with ${scan?.incoming_count ?? "?"} from the snapshot?\n\n` +
      `Only db.questions is touched. Backup created FIRST. Orders/quotes/sessions/responses unaffected.\n\nContinue?`
    )) return;
    setApplying(true); setError(null);
    try {
      const r = await axios.post(`${endpoint}?commit=true`, {}, { withCredentials: true });
      setResult(r.data); playSuccessChime();
    } catch (e) {
      setError(e.response?.data?.detail || e.message || "Apply failed"); playFailureChime();
    } finally { setApplying(false); }
  };

  return (
    <div className="rounded-lg border-2 border-blue-400 bg-white shadow-sm" data-testid="sync-questions-snapshot-card">
      <div className="border-b border-blue-300 bg-blue-50 px-4 py-3">
        <div className="flex items-center gap-2">
          <Database size={18} className="text-blue-700" />
          <h3 className="text-base font-bold text-blue-900">Enforce Full Digital ↔ Print Parity — Sync Questions from Snapshot</h3>
        </div>
        <p className="mt-1 text-xs text-blue-800">
          Replaces this environment's <span className="font-mono">db.questions</span> with the bundled preview snapshot that generated the print BLMs. After sync, digital exam and print BLMs share the same source of truth by definition. Backs up the current collection first. Only <span className="font-mono">db.questions</span> is touched — orders, quotes, commissions, sessions, and responses are left alone.
        </p>
      </div>
      <div className="p-4 space-y-3">
        {lockdown && !lockdown.error && (
          <div
            className={`rounded border p-3 text-sm flex items-start gap-2 ${
              lockdown.matches
                ? "border-emerald-300 bg-emerald-50 text-emerald-900"
                : "border-amber-300 bg-amber-50 text-amber-900"
            }`}
            data-testid="parity-lockdown-status"
          >
            {lockdown.matches ? (
              <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
            ) : (
              <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            )}
            <div className="flex-1">
              <div className="font-semibold">
                {lockdown.matches ? "🔒 PARITY LOCKED" : "⚠ PARITY DRIFT DETECTED"}
              </div>
              <div className="text-xs mt-1">{lockdown.verdict}</div>
              <div className="text-xs mt-1 font-mono">
                db.questions={lockdown.db_count} · snapshot={lockdown.snapshot_count} ·
                {lockdown.last_sync_ts && ` last sync ${lockdown.last_sync_ts.slice(0,19)}`}
              </div>
              <div className="mt-2 flex gap-3 text-xs">
                <button onClick={checkLockdown} className="text-blue-700 underline" data-testid="parity-recheck-btn">Re-check</button>
                <a href="/proof/MOSAIC_Parity_Proof.pdf" target="_blank" rel="noopener" className="text-blue-700 underline" data-testid="parity-proof-download">
                  Download Parity Proof PDF ↓
                </a>
              </div>
            </div>
          </div>
        )}
        {!scan && !error && (
          <Button onClick={runScan} disabled={scanning} data-testid="sync-questions-scan-btn" className="bg-blue-600 hover:bg-blue-700 text-white">
            {scanning ? (<><Loader2 size={16} className="mr-2 animate-spin" />Scanning…</>) : (<>Step 1 — Scan (dry-run)</>)}
          </Button>
        )}
        {scan && (
          <div className="rounded border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="sync-questions-scan-summary">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <div className="text-xs text-slate-500">Current db.questions</div>
                <div className="text-2xl font-bold text-slate-800" data-testid="sync-questions-current-count">{scan.current_prod_count}</div>
              </div>
              <div>
                <div className="text-xs text-slate-500">Snapshot will replace with</div>
                <div className="text-2xl font-bold text-blue-700" data-testid="sync-questions-incoming-count">{scan.incoming_count}</div>
              </div>
              <div className="col-span-2 text-xs"><span className="text-slate-500">Snapshot dated: </span><span className="font-mono">{scan.snapshot?.exported_at}</span></div>
              <div className="col-span-2 text-xs"><span className="text-slate-500">SHA-256: </span><span className="font-mono">{(scan.snapshot?.sha256 || '').slice(0, 20)}…</span></div>
            </div>
          </div>
        )}
        <div className="flex flex-wrap gap-2">
          {scan && (
            <Button onClick={apply} disabled={applying} data-testid="sync-questions-apply-btn" className="bg-emerald-600 hover:bg-emerald-700 text-white">
              {applying ? (<><Loader2 size={16} className="mr-2 animate-spin" />Syncing…</>) : (<>Step 2 — Sync (backs up + replaces)</>)}
            </Button>
          )}
          {scan && (<Button onClick={runScan} disabled={scanning} variant="outline" data-testid="sync-questions-rescan-btn"><RefreshCw size={14} className="mr-1" />Re-scan</Button>)}
        </div>
        {result && (
          <div className="rounded border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-900 flex items-start gap-2" data-testid="sync-questions-result">
            <CheckCircle size={18} className="flex-shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold">{result.message}</div>
              {result.backup_collection && (<div className="text-xs font-mono mt-1">Backup: db.{result.backup_collection} ({result.backup_count} rows)</div>)}
              <div className="text-xs mt-1">Refresh the guest exam and super-admin exam preview to verify.</div>
            </div>
          </div>
        )}
        {error && (
          <div className="rounded border border-red-300 bg-red-50 p-3 text-sm text-red-800 flex items-start gap-2" data-testid="sync-questions-error">
            <AlertTriangle size={18} className="flex-shrink-0 mt-0.5" />
            <div><div className="font-semibold">Error</div><div className="text-xs font-mono mt-1">{String(error)}</div></div>
          </div>
        )}
      </div>
    </div>
  );
}


export default function MaintenanceTab() {
  return (
    <div className="space-y-4" data-testid="maintenance-tab-root">
      <div className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-slate-700 tracking-wide uppercase">
          Maintenance
        </h2>
        <div className="flex items-center gap-3">
          <MaintenanceAudioToggle />
          <HealthCheckIndicator />
        </div>
      </div>

      <div className="bg-blue-50 border-l-4 border-blue-400 p-3">
        <div className="flex items-start gap-2">
          <FileText size={18} className="text-blue-700 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-900">
            <p className="font-bold mb-1">One-click maintenance scripts</p>
            <p>
              Each card runs a dry-run scan first. After reviewing the count,
              type the DB name to confirm and click the red button to delete.
              A JSON receipt with full rollback data is written to{" "}
              <span className="font-mono">/app/memory/</span> and an{" "}
              <span className="font-mono">audit_logs</span> row is appended.
              The most-recent revision of every item is ALWAYS preserved.
            </p>
          </div>
        </div>
      </div>

      <CatalogNamesCard />

      <QuoteMigrationCard />

      <ProductionTriageCard />

      <SyncQuestionsSnapshotCard />

      <MongoStatsCard />

      <DataStateHealthCard />

      <CrossModalityAnchorCard />

      <BulkMissingFilesCard />

      <ReverifyKillSwitchCard />

      <FlyerAttributionCard />

      <ProductionDataMigrationCard />

      <CleanupCard
        title="Dedupe GridFS Revisions"
        description="Removes accumulated stale revisions from the blm_pdfs GridFS bucket. Newest revision of every filename is preserved."
        endpoint="dedupe-gridfs-revisions"
        testIdPrefix="dedupe-gridfs"
        renderSummary={(s) => (
          <>
            {summaryRow("Files in bucket", s.total_files)}
            {summaryRow("Chunks in bucket", s.total_chunks)}
            {summaryRow("Distinct filenames", s.distinct_filenames)}
            {summaryRow("Duplicate groups", s.duplicate_group_count)}
            {summaryRow("Stale revisions to delete", s.losers_total)}
            {summaryRow("Bytes to reclaim", `${s.bytes_to_reclaim_mb} MB`)}
            {s.top_duplicate_groups?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="text-xs font-bold text-gray-600 mb-1">Top duplicate filenames:</div>
                <ul className="text-xs font-mono text-gray-800">
                  {s.top_duplicate_groups.map((g) => (
                    <li key={g.filename}>
                      {g.filename} — {g.revision_count} revs
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      />

      <CleanupCard
        title="Dedupe Questions Collection"
        description="Removes duplicate rows from the questions collection (same canonical id). Newest row by updated_at/created_at is preserved."
        endpoint="dedupe-questions"
        testIdPrefix="dedupe-questions"
        extraBodyOnExecute={{ strategy: "newest_first" }}
        renderSummary={(s) => (
          <>
            {summaryRow("Total rows in collection", s.total_rows)}
            {summaryRow("Distinct ids", s.distinct_ids)}
            {summaryRow("Rows missing id", s.rows_missing_id)}
            {summaryRow("Duplicate groups", s.duplicate_group_count)}
            {summaryRow("Duplicate rows to delete", s.losers_total)}
            {s.top_duplicate_groups?.length > 0 && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="text-xs font-bold text-gray-600 mb-1">Top duplicate ids:</div>
                <ul className="text-xs font-mono text-gray-800">
                  {s.top_duplicate_groups.map((g) => (
                    <li key={g.id}>
                      {g.id?.slice(0, 16)}… — {g.row_count} rows
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      />
    </div>
  );
}
